import { cn } from "cn";
import type {
  CalendarBlock,
  OpeningHours,
} from "@/types/appointments/calendar-types";
import type { TimeRange } from "@/types/appointments/api-types/availability-types";
import { TOTAL_ROWS, ROW_HEIGHT } from "@/constants/appointments/appointments-constants";
import {
  isOddHour,
  isWithinWorkingHours,
  timeToRowIndex,
  durationToRows,
  formatTime,
  rowIndexToTime,
  parseStartTime,
} from "@/lib/appointments/calendar-utils";
import { AppointmentBlock } from "./appointment-block";
import { CreateAppointmentPopover } from "./create-appointment-popover";
import { EditAppointmentPopover } from "./edit-appointment-popover";
import { useDroppable } from "@dnd-kit/core";
import { useState } from "react";
import { useIntl } from "react-intl";
import { isoTime } from "@/lib/utils";

interface StaffColumnProps {
  staffId: string;
  openingHours: OpeningHours;
  workingHours: TimeRange[];
  appointments: CalendarBlock[];
  onAddAppointment: (staffId: string, rowIndex: number) => void;
  onEditAppointment: (appointmentId: number) => void;
  readOnly?: boolean;
}

export function StaffColumn({
  staffId,
  openingHours,
  workingHours,
  appointments,
  onAddAppointment,
  onEditAppointment,
  readOnly = false,
}: StaffColumnProps) {
  const intl = useIntl();
  const [createPopoverOpen, setCreatePopoverOpen] = useState(false);
  const [createRowIndex, setCreateRowIndex] = useState<number | null>(null);
  const [createTimeString, setCreateTimeString] = useState("");
  const [editPopoverOpen, setEditPopoverOpen] = useState(false);
  const [editBlock, setEditBlock] = useState<CalendarBlock | null>(null);
  const [editRowIndex, setEditRowIndex] = useState(0);

  const { setNodeRef } = useDroppable({
    id: staffId,
  });

  const handleCreateSlotClick = (rowIndex: number) => {
    if (readOnly) return;
    if (!isWithinWorkingHours(rowIndex, workingHours)) return;
    const { hour, minute } = rowIndexToTime(rowIndex);
    setCreateTimeString(formatTime(intl, hour, minute));
    setCreateRowIndex(rowIndex);
    setEditPopoverOpen(false);
    setCreatePopoverOpen(true);
  };

  const handleNewAppointmentClick = () => {
    if (createRowIndex === null) return;
    onAddAppointment(staffId, createRowIndex);
    setCreatePopoverOpen(false);
  };

  const handleEditBlockClick = (block: CalendarBlock) => {
    if (readOnly) return;
    const { hour, minute } = parseStartTime(block.start_time);
    setCreatePopoverOpen(false);
    setEditBlock(block);
    setEditRowIndex(timeToRowIndex(hour, minute));
    setEditPopoverOpen(true);
  };

  return (
    <div
      ref={setNodeRef}
      className="min-w-56 flex-1 border-r relative"
    >
      {Array.from({ length: TOTAL_ROWS }).map((_, rowIndex) => {
        const isAlternate = isOddHour(rowIndex, openingHours);
        const isOpen = isWithinWorkingHours(rowIndex, workingHours);

        return (
          <div
            key={rowIndex}
            onClick={
              isOpen && !readOnly
                ? () => handleCreateSlotClick(rowIndex)
                : undefined
            }
            className={cn(
              "border-b transition-colors",
              !isOpen && "bg-border",
              isOpen && !readOnly && "cursor-pointer",
              isOpen && (isAlternate ? "bg-secondary" : "bg-background")
            )}
            style={{ height: `${ROW_HEIGHT}px` }}
          />
        );
      })}

      {appointments.map((block) => {
        const { hour, minute } = parseStartTime(block.start_time);
        return (
          <AppointmentBlock
            key={block.id}
            block={block}
            startRow={timeToRowIndex(hour, minute)}
            durationRows={durationToRows(block.duration_minutes)}
            onEditClick={handleEditBlockClick}
            readOnly={readOnly || block.status === "completed"}
          />
        );
      })}

      {createRowIndex !== null && (
        <CreateAppointmentPopover
          rowIndex={createRowIndex}
          timeString={createTimeString}
          open={createPopoverOpen}
          onOpenChange={setCreatePopoverOpen}
          onNewAppointmentClick={handleNewAppointmentClick}
        />
      )}

      {editBlock && (
        <EditAppointmentPopover
          rowIndex={editRowIndex}
          clientName={editBlock.client_name}
          timeString={intl.formatTime(isoTime(editBlock.start_time), { format: "clock" })}
          status={editBlock.status}
          open={editPopoverOpen}
          onOpenChange={setEditPopoverOpen}
          onEditAppointmentClick={() => {
            onEditAppointment(editBlock.appointment_id);
            setEditPopoverOpen(false);
          }}
        />
      )}
    </div>
  );
}
